// The answers keys the intake flow persists a member's baseline week under.
// Only flow-state owners name them: every other reader takes the week from the
// baselines module's canonical week. A client that lists answers still needs to
// recognise them, so it can leave out the keys a baseline section already shows.

const BASELINE_DAY_ANSWER_KEY = /^(?:typical_)?day_([1-7])$/;
const TYPICAL_DAY_ANSWER_KEY = /^typical_day_[1-7]$/;
const WEEK_TYPICAL_ANSWER_KEY = 'week_typical';
const WEEK_ANCHOR_ANSWER_KEY = 'week_anchor_date';
const BASELINE_WEEK_ANSWER_KEYS: ReadonlySet<string> = new Set([
  WEEK_TYPICAL_ANSWER_KEY,
  WEEK_ANCHOR_ANSWER_KEY,
]);

/** One of the fourteen per-day baseline answers (reported or typical week), by bare key. */
export function isBaselineDayAnswerKey(bareKey: string): boolean {
  return BASELINE_DAY_ANSWER_KEY.test(bareKey);
}

/**
 * The day ordinal N a per-day baseline answer holds (1 is the day before the
 * anchor), or null when the key is not one. The same N names the reported day
 * dated N days before capture and the typical day on that day's weekday, so a
 * reader can match an answer to the baseline entry it became without naming
 * the keys itself.
 */
export function baselineDayAnswerOrdinal(bareKey: string): number | null {
  const match = BASELINE_DAY_ANSWER_KEY.exec(bareKey);
  return match?.[1] === undefined ? null : Number(match[1]);
}

/** Any answer the baseline week is captured under: the day answers, typicality and the anchor. */
export function isBaselineAnswerKey(bareKey: string): boolean {
  return isBaselineDayAnswerKey(bareKey) || BASELINE_WEEK_ANSWER_KEYS.has(bareKey);
}

/** One of the seven typical-week day answers, by bare key. */
export function isTypicalDayAnswerKey(bareKey: string): boolean {
  return TYPICAL_DAY_ANSWER_KEY.test(bareKey);
}

/** The member's answer to whether the reported week was typical, by bare key. */
export function isWeekTypicalAnswerKey(bareKey: string): boolean {
  return bareKey === WEEK_TYPICAL_ANSWER_KEY;
}

/** The anchor date the reported week is dated back from, by bare key. */
export function isWeekAnchorAnswerKey(bareKey: string): boolean {
  return bareKey === WEEK_ANCHOR_ANSWER_KEY;
}
